import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import downloadImg from '../images/download.png'
import trashImg from '../images/trash.png'
import addCustomerImg from '../images/add-customer.png'
import removeCustomerImg from '../images/remove-customer.png'

import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

type InvoiceFormProps = {
  setPage: (page: string) => void;
  notify: () => void;
  setNotificationMessage:  (page: string) => void;
  confirm: (message: string) => Promise<boolean>;
  setIsDeletePopupVisible: (val: boolean) => void;
  isDeletePopupVisible: boolean;
};

type Customer = {
  id: number;
  firstName: string;
  lastName: string;
};

type Student = {
  id: number;
  name: string;
};

const InvoiceForm: React.FC<InvoiceFormProps> = ({ setPage, notify,setNotificationMessage, confirm,setIsDeletePopupVisible, isDeletePopupVisible }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [students, setStudents] = useState<Student[]>([]);
  const [studentData, setStudentData] = useState<
    { [studentId: string]: { fees: string; hours: string } }
  >({});
  const [files,setFiles] = useState<string[]>();

  const customerRef = useRef<HTMLSelectElement>(null);
  const studentRefs = useRef<{ fees: HTMLInputElement | null; hours: HTMLInputElement | null }[]>([]);
  const invoiceDateRef = useRef<HTMLInputElement>(null);
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch("https://lscinvoice.com/api/get-customers/");
        if (!response.ok) throw new Error("Failed to fetch customers");
        const data = await response.json();
        setCustomers(data);
      } catch (err) {
        console.error("Error fetching customers:", err);
      }
    };

    fetchCustomers();
  }, [,isDeletePopupVisible]);

  const fetchFiles = async () => {
    try {
      const response = await fetch(`https://lscinvoice.com/api/get-files/${selectedCustomerId}`);
      if (!response.ok) throw new Error("Failed to fetch customers");
      const data = await response.json();
      setFiles(data.files);
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [,selectedCustomerId]);

  useEffect(() => {
    if (!selectedCustomerId) return;

    const fetchStudents = async () => {
      try {
        const response = await fetch(
          `https://lscinvoice.com/api/get-details/${selectedCustomerId}`
        );
        if (!response.ok) throw new Error("Failed to fetch students");
        const data = await response.json();
        setStudents(data);

        const initialData: { [id: string]: { fees: string; hours: string } } =
          {};
        data.forEach((student: Student) => {
          initialData[student.id] = { fees: "", hours: "" };
        });
        setStudentData(initialData);
      } catch (err) {
        console.error("Error fetching students:", err);
      }
    };

    fetchStudents();
  }, [selectedCustomerId]);

  const handleStudentInputChange = (
    studentId: string,
    field: "fees" | "hours",
    value: string
  ) => {
    if (value) {
      if (value.includes('.')) {
        const parts = value.split('.');
        if (parts[1].length > 2) {
          value = parts[0] + '.' + parts[1].substring(0, 2);
        }
      }
    }
    setStudentData((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [field]: value },
    }));
  };

  const handleAddCustomer = () => {
    setPage("addData");
  };

  const handleGenerateInvoice = async () => {
    let hasError = false;

    if (customerRef.current) {
      if (!customerRef.current.value) {
        customerRef.current.classList.add("error");
        hasError = true;
      } else {
        customerRef.current.classList.remove("error");
      }
    }

    studentRefs.current.forEach((ref) => {
      if (ref.fees) {
        if (!ref.fees.value.trim()) {
          ref.fees.classList.add("error");
          hasError = true;
        } else {
          ref.fees.classList.remove("error");
        }
      }
    });

    if (hasError) {
      setNotificationMessage("Please fill all required fields!");
      notify();
      return;
    }

    try {
      const fees_per_week: string[] = [];
      const hours_per_week: string[] = [];

      students.forEach((s) => {
        fees_per_week.push(studentData[s.id]?.fees || "0");
        hours_per_week.push(studentData[s.id]?.hours || "");
      });

      const payload = {
        customer_id: parseInt(selectedCustomerId),
        invoice_date: invoiceDate,
        from_date: startDate,
        to_date: endDate,
        fees_per_week,
        hours_per_week,
      };

      const response = await fetch("https://lscinvoice.com/api/generate/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to generate invoice");

      const data = await response.json();
      console.log("Invoice generated:", data);
      await fetchFiles();
      setNotificationMessage("Invoice generated successfully!");
      notify();
    } catch (err) {
      console.error("Error generating invoice:", err);
      setNotificationMessage("Failed to generate invoice");
      notify();
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      const response = await fetch(`https://lscinvoice.com/api/download/${selectedCustomerId}/${filename}`);
      if (!response.ok) throw new Error("Failed to download");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const handleDelete = async (filename: string) => {
    const ok = await confirm(`Delete ${filename}?`);
    if (!ok) return;
    try {  
      const response = await fetch(`https://lscinvoice.com/api/delete/${selectedCustomerId}/${filename}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete");
      await fetchFiles();
      return await response.json();
    } catch (err) {
      console.error("Error deleting file:", err);
    }
  };

  const handleRemoveCustomer = async () => {
    setIsDeletePopupVisible(true);
  }

  useEffect(() => {
    if (invoiceDateRef.current) {
      flatpickr(invoiceDateRef.current, {
        dateFormat: "d/m/Y",
        allowInput: true,
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            const iso = selectedDates[0].toISOString().split("T")[0];
            setInvoiceDate(iso);
          }
        },
      });
    }

    if (startDateRef.current) {
      flatpickr(startDateRef.current, {
        dateFormat: "d/m/Y",
        allowInput: true,
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            const iso = selectedDates[0].toISOString().split("T")[0];
            setStartDate(iso);
          }
        },
      });
    }

    if (endDateRef.current) {
      flatpickr(endDateRef.current, {
        dateFormat: "d/m/Y",
        allowInput: true,
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            const iso = selectedDates[0].toISOString().split("T")[0];
            setEndDate(iso);
          }
        },
      });
    }
  }, []);


  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}  
      className="flex">
      <div className="align-left align-right builder-flex">
          <div className="builder-div build-align-right mt-5 mb-5 bg-white">
          <div className="flex">
            <label>Customer:</label>
            <select
              ref={customerRef}
              className="align-right"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              <option value="">Customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.id}: {c.firstName} {c.lastName}
                </option>
              ))}
            </select>
            <button
              className="align-right customer-button flex"
              onClick={handleAddCustomer}
            >
              <img className="icons" src={addCustomerImg} />
            </button>
            <button
              className="align-right customer-button flex"
              onClick={handleRemoveCustomer}
            >
              <img className="icons" src={removeCustomerImg} />
            </button>
          </div>

          <div className="flex">
            <label>Invoice Date:</label>
            <input
              ref={invoiceDateRef}
              className="align-right"
              type="text"
              placeholder="dd/mm/yyyy"
            />
          </div>

          <div className="flex">
            <input
              ref={startDateRef}
              className="text-c"
              type="text"
              placeholder="dd/mm/yyyy"
            />
            <label className="align-right to-label">To</label>
            <input
              ref={endDateRef}
              className="text-c"
              type="text"
              placeholder="dd/mm/yyyy"
            />
          </div>

          {students.map((s, index) => (
            <div key={s.id}>
              <div className="flex">
                <label className="align-left student-label">{s.name.split(' ')[0]}'s fees/week:</label>
                <input
                  ref={(el) => {
                    if (!studentRefs.current[index]) studentRefs.current[index] = { fees: null, hours: null };
                    studentRefs.current[index].fees = el;
                  }}
                  className="align-right small-input"
                  type="number"
                  value={studentData[s.id]?.fees || ""}
                  step="0.01"
                  min="0"
                  onChange={(e) =>
                    setStudentData((prev) => ({
                      ...prev,
                      [s.id]: { ...prev[s.id], fees: e.target.value },
                    }))
                  }
                />
              </div>
              
              <div className="flex">
                <label className="align-left student-label">{s.name}'s hours/week:</label>
                <input
                  className="align-right small-input"
                  type="number"
                  value={studentData[s.id]?.hours || ""}
                  min="0"
                  onChange={(e) =>
                    handleStudentInputChange(s.id.toString(), "hours", e.target.value)
                  }
                />
              </div>
            </div>
          ))}

          <div className="flex generate-button">
            <motion.button
              initial={{
                backgroundImage:
                  "linear-gradient(to top, rgba(0, 110, 0, 0), white, white)",
              }}
              whileHover={{
                backgroundImage:
                  "linear-gradient(to top, rgba(0, 110, 0, 0.3), white, white)",
              }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="align-right align-left green-button"
              onClick={handleGenerateInvoice}
            >
              Generate Invoice
            </motion.button>
          </div>
        </div>
        <div className="build-align-left files-display-div mt-5 mb-5 bg-white">
          <div className="files-display-inner-div">
            {files && files.map((f) => (
                  <p className="file flex" id={f}>{f}
                    <div className="flex align-right">
                      <img className="download" src={downloadImg} onClick={() => {handleDownload(f)}}/>
                      <img className="trash" src={trashImg} onClick={() => {handleDelete(f)}}/>
                    </div>
                  </p>
              ))}
          </div>
        </div>
      </div>
      
    </motion.div>
  );
};

export default InvoiceForm;
